import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { AlertTriangle, RefreshCw, Filter, X } from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

// Compact date/time — mirrors AdminAuditLog for visual consistency.
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

// Buffer-fill tone: green well below cap, amber approaching, red at/over
// (soft cap → trim-on-write triggers; not a failure but a signal that
// audit window is getting compressed).
function bufferTone(size, cap) {
  const pct = cap > 0 ? (size / cap) : 0;
  if (pct >= 0.9) return "bg-[#fdecec] text-[#a12b2b] border-[#f5c5c5]";
  if (pct >= 0.6) return "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]";
  return "bg-[#e6f9f2] text-[#0e5a48] border-[#c1eddc]";
}

// Endpoint pill — small tag for quick visual grouping in the table.
function endpointPill(ep) {
  const short = ep?.replace(/^\/api\//, "") || "?";
  return (
    <span className="inline-block text-[10.5px] font-mono-data px-1.5 py-0.5 rounded
                     bg-[#eef1f6] text-[#33445e] border border-[#dde3ec]">
      {short}
    </span>
  );
}

export default function AdminIngestWarns() {
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [refreshing, setRefreshing] = useState(false);
  const [endpointFilter, setEndpointFilter] = useState("");
  const [view, setView] = useState("summary"); // "summary" | "items"

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoadState({ status: "loading", error: null });
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (endpointFilter) params.set("endpoint", endpointFilter);
      const { data } = await api.get(`/admin/ingest-warns?${params}`);
      setData(data);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setData(null);
      setLoadState({ status: "error", error: extractApiError(err) });
    } finally {
      setRefreshing(false);
    }
  }, [endpointFilter]);

  useEffect(() => { load(); }, [load]);

  // Distinct endpoints across the summary — used to populate the filter
  // dropdown so the admin can drill without typing.
  const distinctEndpoints = useMemo(() => {
    if (!data?.summary?.length) return [];
    return [...new Set(data.summary.map((r) => r.endpoint))].sort();
  }, [data]);

  if (loadState.status === "loading" && !data) {
    return (
      <div className="max-w-7xl mx-auto" data-testid="admin-ingest-warns-page">
        <Header />
        <PanelState state="loading" />
      </div>
    );
  }
  if (loadState.status === "error") {
    return (
      <div className="max-w-7xl mx-auto" data-testid="admin-ingest-warns-page">
        <Header />
        <PanelState state="error" error={loadState.error} onRetry={() => load()} />
      </div>
    );
  }

  const size = data?.buffer_size ?? 0;
  const cap = data?.buffer_capacity ?? 5000;
  const retention = data?.retention_days ?? 30;
  const totalSummaryRows = data?.summary?.length ?? 0;
  const showEmpty = size === 0;

  return (
    <div className="max-w-7xl mx-auto space-y-5" data-testid="admin-ingest-warns-page">
      <Header />

      {/* Buffer / retention banner */}
      <div className={`border rounded-lg p-3.5 flex items-start gap-3 ${bufferTone(size, cap)}`}
        data-testid="ingest-warns-banner">
        <AlertTriangle size={18} strokeWidth={1.8} className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold">
            Tier 1 unknown-field audit — durable store
          </div>
          <div className="text-[11.5px] font-mono-data mt-1 opacity-90 flex flex-wrap gap-x-4 gap-y-1">
            <span>records={size}/{cap}</span>
            <span>retention={retention}d</span>
            <span>unique_patterns={totalSummaryRows}</span>
          </div>
          <div className="text-[11.5px] mt-1.5 opacity-90">
            Each row = one POST to a Tier-1-registered ingest endpoint carrying
            top-level fields not declared in that endpoint&apos;s Pydantic model.
            Fields are dropped silently today (extra=&quot;ignore&quot;). Use the summary
            below to decide, per row: fix the source agent, or extend the schema
            to accept the field on purpose.
          </div>
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
          className="p-1.5 rounded-md hover:bg-white/60 disabled:opacity-50"
          data-testid="ingest-warns-refresh"
          aria-label="Refresh">
          <RefreshCw size={14} strokeWidth={1.8} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      {/* View toggle + filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-[#f5f6f8] rounded-md p-0.5 text-[12.5px]" role="tablist">
          <button
            onClick={() => setView("summary")}
            className={`px-3 py-1.5 rounded font-medium transition-colors ${
              view === "summary" ? "bg-white shadow-sm text-[#1e2c48]" : "text-[#5d6a80]"
            }`}
            data-testid="view-summary-btn"
          >Summary</button>
          <button
            onClick={() => setView("items")}
            className={`px-3 py-1.5 rounded font-medium transition-colors ${
              view === "items" ? "bg-white shadow-sm text-[#1e2c48]" : "text-[#5d6a80]"
            }`}
            data-testid="view-items-btn"
          >Recent Items</button>
        </div>
        <div className="flex items-center gap-2 text-[12.5px] text-[#5d6a80]">
          <Filter size={13} strokeWidth={1.8} />
          <select
            value={endpointFilter}
            onChange={(e) => setEndpointFilter(e.target.value)}
            className="border border-[#dde3ec] rounded px-2 py-1 bg-white text-[#1e2c48]"
            data-testid="endpoint-filter-select"
          >
            <option value="">All endpoints</option>
            {distinctEndpoints.map((ep) => (
              <option key={ep} value={ep}>{ep}</option>
            ))}
          </select>
          {endpointFilter && (
            <button
              onClick={() => setEndpointFilter("")}
              className="text-[11.5px] text-[#5d6a80] hover:text-[#1e2c48] flex items-center gap-0.5"
              data-testid="clear-endpoint-filter"
            ><X size={12} /> clear</button>
          )}
        </div>
      </div>

      {showEmpty ? (
        <EmptyState endpointFilter={endpointFilter} />
      ) : view === "summary" ? (
        <SummaryTable rows={data.summary} />
      ) : (
        <ItemsTable rows={data.items} />
      )}
    </div>
  );
}

function Header() {
  return (
    <div className="space-y-1">
      <div className="text-[11.5px] uppercase tracking-widest text-[#8090aa] font-semibold">
        Platform Admin
      </div>
      <h1 className="text-2xl font-semibold text-[#1e2c48] flex items-center gap-2">
        <AlertTriangle size={22} strokeWidth={1.8} className="text-[#8a5b00]" />
        Ingest Warnings
      </h1>
      <p className="text-[13px] text-[#5d6a80]">
        Tier 1 pre-flip audit surface. Persisted to MongoDB with 30-day retention.
      </p>
    </div>
  );
}

function SummaryTable({ rows }) {
  return (
    <div className="border border-[#e7e9ee] rounded-lg bg-white overflow-hidden"
      data-testid="summary-table">
      <table className="w-full text-[12.5px]">
        <thead className="bg-[#fafbfc] border-b border-[#e7e9ee]">
          <tr className="text-left text-[10.5px] uppercase tracking-wider text-[#8090aa]">
            <th className="px-4 py-2.5 font-semibold">Endpoint</th>
            <th className="px-4 py-2.5 font-semibold">Unknown Fields</th>
            <th className="px-4 py-2.5 font-semibold text-right">Count</th>
            <th className="px-4 py-2.5 font-semibold">First Seen</th>
            <th className="px-4 py-2.5 font-semibold">Last Seen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className="border-b border-[#eef1f6] last:border-0 hover:bg-[#fafbfc]"
              data-testid={`summary-row-${idx}`}>
              <td className="px-4 py-3 align-top">{endpointPill(r.endpoint)}</td>
              <td className="px-4 py-3 align-top">
                <div className="flex flex-wrap gap-1">
                  {r.unknown.map((f) => (
                    <code key={f} className="text-[11px] font-mono-data px-1.5 py-0.5 rounded
                      bg-[#fff4d6] text-[#8a5b00] border border-[#f3d97a]">
                      {f}
                    </code>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3 align-top text-right font-mono-data font-semibold">{r.count}</td>
              <td className="px-4 py-3 align-top text-[#5d6a80]">{fmtDateTime(r.first_seen)}</td>
              <td className="px-4 py-3 align-top text-[#5d6a80]">{fmtDateTime(r.last_seen)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ItemsTable({ rows }) {
  return (
    <div className="border border-[#e7e9ee] rounded-lg bg-white overflow-hidden"
      data-testid="items-table">
      <table className="w-full text-[12.5px]">
        <thead className="bg-[#fafbfc] border-b border-[#e7e9ee]">
          <tr className="text-left text-[10.5px] uppercase tracking-wider text-[#8090aa]">
            <th className="px-4 py-2.5 font-semibold">Timestamp</th>
            <th className="px-4 py-2.5 font-semibold">Endpoint</th>
            <th className="px-4 py-2.5 font-semibold">Tenant</th>
            <th className="px-4 py-2.5 font-semibold">Unknown Fields</th>
            <th className="px-4 py-2.5 font-semibold text-right">Declared #</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className="border-b border-[#eef1f6] last:border-0 hover:bg-[#fafbfc]"
              data-testid={`item-row-${idx}`}>
              <td className="px-4 py-3 align-top text-[#5d6a80] font-mono-data text-[11.5px]">{fmtDateTime(r.ts)}</td>
              <td className="px-4 py-3 align-top">{endpointPill(r.endpoint)}</td>
              <td className="px-4 py-3 align-top text-[#5d6a80]">{r.tenant_hint || "—"}</td>
              <td className="px-4 py-3 align-top">
                <div className="flex flex-wrap gap-1">
                  {r.unknown.map((f) => (
                    <code key={f} className="text-[11px] font-mono-data px-1.5 py-0.5 rounded
                      bg-[#fff4d6] text-[#8a5b00] border border-[#f3d97a]">{f}</code>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3 align-top text-right font-mono-data text-[#5d6a80]">{r.declared_field_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ endpointFilter }) {
  return (
    <div className="border border-dashed border-[#dde3ec] rounded-lg bg-[#fafbfc] p-10 text-center"
      data-testid="ingest-warns-empty">
      <AlertTriangle size={28} strokeWidth={1.5} className="mx-auto text-[#8090aa] mb-3" />
      <div className="text-[14px] font-semibold text-[#1e2c48]">
        {endpointFilter ? `No warnings for ${endpointFilter}` : "No warnings recorded"}
      </div>
      <div className="text-[12.5px] text-[#5d6a80] mt-1 max-w-md mx-auto">
        {endpointFilter
          ? "Nothing matched your endpoint filter. Clear it to see the full store."
          : "This is a clean signal — every ingest payload received since the last purge has stayed inside its declared schema. Safe to consider the Tier 1 flip once real agent traffic has accumulated over a stable window."}
      </div>
    </div>
  );
}
